import { useCallback, useReducer } from 'react'
import { submitToFormspree } from '@/lib/formspree'
import { RETRY_CONFIG } from '@/lib/constants'

export type FormStatus = 'idle' | 'loading' | 'success' | 'error'

interface FormState {
  status: FormStatus
  error: string | null
}

type FormAction =
  | { type: 'SUBMIT_START' }
  | { type: 'SUBMIT_SUCCESS' }
  | { type: 'SUBMIT_ERROR'; error: string }
  | { type: 'RESET' }

function reducer(state: FormState, action: FormAction): FormState {
  switch (action.type) {
    case 'SUBMIT_START':
      return { status: 'loading', error: null }
    case 'SUBMIT_SUCCESS':
      return { status: 'success', error: null }
    case 'SUBMIT_ERROR':
      return { status: 'error', error: action.error }
    case 'RESET':
      return { status: 'idle', error: null }
  }
}

const NON_RETRYABLE_CODES = new Set(['VALIDATION_ERROR', 'RATE_LIMITED', 'MISSING_FORM_ID'])

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export interface UseFormspreeReturn {
  status: FormStatus
  error: string | null
  submit: (data: Record<string, unknown>) => Promise<void>
  reset: () => void
}

export function useFormspree(formId: string): UseFormspreeReturn {
  const [state, dispatch] = useReducer(reducer, { status: 'idle', error: null })

  const submit = useCallback(
    async (data: Record<string, unknown>) => {
      if (state.status === 'loading') return

      dispatch({ type: 'SUBMIT_START' })

      for (let attempt = 0; attempt < RETRY_CONFIG.MAX_ATTEMPTS; attempt++) {
        if (attempt > 0) {
          await sleep(RETRY_CONFIG.BACKOFF_MS[attempt - 1])
        }

        const result = await submitToFormspree(formId, data)

        if (result.success) {
          dispatch({ type: 'SUBMIT_SUCCESS' })
          return
        }

        if (NON_RETRYABLE_CODES.has(result.code ?? '')) {
          dispatch({ type: 'SUBMIT_ERROR', error: result.message })
          return
        }

        if (attempt === RETRY_CONFIG.MAX_ATTEMPTS - 1) {
          dispatch({ type: 'SUBMIT_ERROR', error: result.message })
          return
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formId, state.status]
  )

  const reset = useCallback(() => {
    dispatch({ type: 'RESET' })
  }, [])

  return { status: state.status, error: state.error, submit, reset }
}
